import { call, put, takeLatest } from "redux-saga/effects";
import axios from "axios";
import { toast } from "react-toastify";
import {
  fetchTemplatesRequest,
  fetchTemplatesSuccess,
  fetchTemplatesFailure,
  createTemplateRequest,
  createTemplateSuccess,
  createTemplateFailure,
  updateTemplateStatusRequest,
  updateTemplateStatusSuccess,
  updateTemplateStatusFailure,
  deleteTemplateRequest,
  deleteTemplateSuccess,
  deleteTemplateFailure,
  fetchCouponsRequest,
  fetchCouponsSuccess,
  fetchCouponsFailure,
  fetchCouponTransactionsRequest,
  fetchCouponTransactionsSuccess,
  fetchCouponTransactionsFailure,
} from "./customerCouponSlice";

const base = process.env.REACT_APP_API_BASE_URL;

function* fetchTemplates(action) {
  try {
    const payload = action.payload || { start: 0, limit: 10 };
    const response = yield call(() =>
      axios.get(`${base}/api/v1/customer-coupon/reward-template`, {
        params: payload,
      })
    );
    const apiData = response.data?.data;
    yield put(
      fetchTemplatesSuccess({
        data: apiData?.data || [],
        total: apiData?.recordsTotal || 0,
      })
    );
  } catch (error) {
    yield put(fetchTemplatesFailure(error.message));
    toast.error("Failed to load reward templates");
  }
}

function* createTemplate(action) {
  try {
    const payload = action.payload || {};
    const formData = new FormData();
    formData.append("title", payload.title || "");
    formData.append("subtitle", payload.subtitle || "");
    formData.append("description", payload.description || "");
    formData.append("customMessage", payload.customMessage || "");
    formData.append("type", payload.type || "lucky_draw");
    formData.append("status", payload.status || "active");
    formData.append("jewellerScope", payload.jewellerScope || "custom");
    formData.append(
      "jewellers",
      JSON.stringify(
        payload.jewellerScope === "all" ? [] : payload.jewellers || [],
      ),
    );
    if (payload.imageFile) {
      formData.append("image", payload.imageFile);
    }

    // Do not set Content-Type manually — axios must add the multipart boundary
    // or Nest will not receive fields like title.
    const response = yield call(() =>
      axios.post(`${base}/api/v1/customer-coupon/reward-template`, formData)
    );
    const { success, message } = response.data || {};
    if (!success) {
      yield put(createTemplateFailure(message));
      toast.error(message || "Could not create reward template");
      return;
    }
    yield put(createTemplateSuccess());
    toast.success(message || "Reward template created");
    yield put(fetchTemplatesRequest({ start: 0, limit: 10 }));
  } catch (error) {
    yield put(createTemplateFailure(error.message));
    toast.error("Failed to create reward template");
  }
}

function* updateTemplateStatus(action) {
  try {
    const { id, status, listQuery } = action.payload || {};
    const response = yield call(() =>
      axios.post(`${base}/api/v1/customer-coupon/reward-template/status`, {
        id,
        status,
      })
    );
    const { success, message } = response.data || {};
    if (!success) {
      yield put(updateTemplateStatusFailure(message));
      toast.error(message || "Could not update status");
      return;
    }
    yield put(updateTemplateStatusSuccess());
    toast.success(message || "Status updated");
    yield put(fetchTemplatesRequest(listQuery || { start: 0, limit: 10 }));
  } catch (error) {
    yield put(updateTemplateStatusFailure(error.message));
    toast.error("Failed to update status");
  }
}

function* deleteTemplate(action) {
  try {
    const response = yield call(() =>
      axios.post(`${base}/api/v1/customer-coupon/reward-template/delete`, {
        id: action.payload.id,
      })
    );
    const { success, message } = response.data || {};
    if (!success) {
      yield put(deleteTemplateFailure(message));
      toast.error(message || "Could not delete reward template");
      return;
    }
    yield put(deleteTemplateSuccess());
    toast.success(message || "Reward template deleted");
    yield put(
      fetchTemplatesRequest(action.payload.listQuery || { start: 0, limit: 10 })
    );
  } catch (error) {
    yield put(deleteTemplateFailure(error.message));
    toast.error("Failed to delete reward template");
  }
}

function* fetchCoupons(action) {
  try {
    const payload = action.payload || { start: 0, limit: 10 };
    const response = yield call(() =>
      axios.get(`${base}/api/v1/customer-coupon/issued`, { params: payload })
    );
    const apiData = response.data?.data;
    yield put(
      fetchCouponsSuccess({
        data: apiData?.data || [],
        total: apiData?.recordsTotal || 0,
      })
    );
  } catch (error) {
    yield put(fetchCouponsFailure(error.message));
    toast.error("Failed to load issued coupons");
  }
}

function* fetchCouponTransactions(action) {
  try {
    const payload = action.payload || { start: 0, limit: 10 };
    const response = yield call(() =>
      axios.get(`${base}/api/v1/customer-coupon/issued/transactions`, {
        params: payload,
      })
    );
    const apiData = response.data?.data;
    if (response.data?.success === false) {
      yield put(
        fetchCouponTransactionsFailure(
          response.data?.message || "Failed to load transactions"
        )
      );
      toast.error(response.data?.message || "Failed to load transactions");
      return;
    }
    yield put(
      fetchCouponTransactionsSuccess({
        data: apiData?.data || [],
        total: apiData?.recordsTotal || 0,
        coupon: apiData?.coupon || null,
      })
    );
  } catch (error) {
    yield put(fetchCouponTransactionsFailure(error.message));
    toast.error("Failed to load coupon transactions");
  }
}

export function* customerCouponSaga() {
  yield takeLatest(fetchTemplatesRequest.type, fetchTemplates);
  yield takeLatest(createTemplateRequest.type, createTemplate);
  yield takeLatest(updateTemplateStatusRequest.type, updateTemplateStatus);
  yield takeLatest(deleteTemplateRequest.type, deleteTemplate);
  yield takeLatest(fetchCouponsRequest.type, fetchCoupons);
  yield takeLatest(fetchCouponTransactionsRequest.type, fetchCouponTransactions);
}
